import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { parseNativeCapabilities, parseNativePreparation, type NativePreparation, type NativeSourceCapability, type PlaybackState, type PlayerCommand } from '@streamit/contracts';
import { api, ApiError } from '@/lib/api';
import { useSession } from './session';
import type { SourcePreference } from '@/lib/episode-playback';
export type Connection = {
  online: boolean;
  connected: boolean;
  state: PlaybackState | null;
  nativeSources?: NativeSourceCapability[];
  nativePreparation?: NativePreparation | null;
  error?: string;
};
type Remote = {
  connections: Record<string, Connection>;
  selectedId?: string;
  select: (id?: string) => void;
  command: (deviceId: string, command: PlayerCommand) => boolean;
  rememberSource?: (requestId: string, preference: SourcePreference) => void;
};
const Context = createContext<Remote>({
  connections: {},
  select: () => {},
  command: () => false,
});
export const ReceiverRemoteProvider = Context.Provider;
export const useRemote = () => useContext(Context);
function commandError(code: unknown) {
  if (code === 'DEVICE_OFFLINE') return 'Open Streamit on this screen to control it.';
  if (code === 'PLAYBACK_CHANGED' || code === 'NO_PLAYBACK') return 'Playback changed. Use the current video’s controls.';
  if (code === 'PLAYER_LOADING') return 'Wait for this player to finish opening.';
  if (code === 'SUBTITLE_TIMING_UNAVAILABLE') return 'Subtitle timing is unavailable for this player or source.';
  if (code === 'COMMAND_EXPIRED') return 'The command expired. Try again.';
  if (code === 'NATIVE_SELECTION_CHANGED' || code === 'SOURCE_EXPIRED') return 'The file choices changed or expired. Choose the source again.';
  return 'The screen could not complete that action.';
}
export function RemoteProvider({ children }: { children: ReactNode }) {
  const session = useSession(),
    [connections, setConnections] = useState<Record<string, Connection>>({}),
    [selectedId, select] = useState<string>();
  const sockets = useRef(new Map<string, WebSocket>()),
    connectionsRef = useRef(connections);
  connectionsRef.current = connections;
  const userId = session.data?.user.id;
  const ids = (session.data?.devices || [])
    .map((d) => d.id)
    .sort()
    .join(',');
  const refreshRef = useRef(session.refresh);
  refreshRef.current = session.refresh;
  useEffect(() => select(undefined), [userId]);
  useEffect(() => {
    let disposed = false;
    setConnections({});
    const timers = new Map<string, ReturnType<typeof setTimeout>>(),
      beats = new Map<string, ReturnType<typeof setInterval>>(),
      preparationTimers = new Map<string, ReturnType<typeof setTimeout>>(),
      preparations = new Map<string, NativePreparation>(),
      capabilities = new Map<string, NativeSourceCapability[]>(),
      attempts = new Map<string, number>(),
      connecting = new Set<string>();
    const update = (id: string, value: Partial<Connection>) => {
      if (!disposed)
        setConnections((old) => ({
          ...old,
          [id]: {
            ...(old[id] || { online: false, connected: false, state: null }),
            ...value,
          },
        }));
    };
    const clearPreparation = (id: string) => {
      clearTimeout(preparationTimers.get(id));
      preparationTimers.delete(id);
      preparations.delete(id);
      update(id, { nativePreparation: null });
    };
    const clearNative = (id: string) => {
      clearPreparation(id);
      capabilities.delete(id);
      update(id, { nativeSources: [] });
    };
    const receiveCapabilities = (id: string, value: unknown) => {
      const supported = parseNativeCapabilities(value);
      capabilities.set(id, supported);
      update(id, { nativeSources: supported });
      if (!supported.length) clearPreparation(id);
    };
    const receivePreparation = (id: string, value: unknown) => {
      const next = parseNativePreparation(value);
      if (!next || !capabilities.get(id)?.length) {
        clearPreparation(id);
        return;
      }
      const previous = preparations.get(id);
      if (previous?.sessionId === next.sessionId && next.revision < previous.revision) return;
      clearPreparation(id);
      if (next.expiresAt <= Date.now()) {
        update(id, { error: 'Source selection expired. Choose the source again.' });
        return;
      }
      preparations.set(id, next);
      update(id, { nativePreparation: next, error: undefined });
      const expire = () => {
        if (disposed || preparations.get(id) !== next) return;
        const remaining = next.expiresAt - Date.now();
        if (remaining > 0) {
          preparationTimers.set(id, setTimeout(expire, Math.min(remaining, 120000)));
          return;
        }
        clearPreparation(id);
        update(id, { error: 'Source selection expired. Choose the source again.' });
      };
      preparationTimers.set(id, setTimeout(expire, Math.min(next.expiresAt - Date.now(), 120000)));
    };
    const retry = (id: string) => {
      if (disposed || timers.has(id)) return;
      const n = (attempts.get(id) || 0) + 1;
      attempts.set(id, n);
      timers.set(
        id,
        setTimeout(
          () => {
            timers.delete(id);
            void connect(id);
          },
          Math.min(60000, 1000 * 2 ** Math.min(n, 6)) + Math.random() * 500,
        ),
      );
    };
    const connect = async (id: string) => {
      if (disposed || connecting.has(id)) return;
      const existing = sockets.current.get(id);
      if (
        existing &&
        (existing.readyState === WebSocket.OPEN ||
          existing.readyState === WebSocket.CONNECTING)
      )
        return;
      connecting.add(id);
      try {
        const { ticket, path } = await api<{ ticket: string; path: string }>(
          `/devices/${id}/ticket`,
          { method: 'POST' },
        );
        if (disposed) return;
        const endpoint = new URL(path, location.origin);
        if (endpoint.origin !== location.origin)
          throw new Error('Invalid realtime endpoint');
        endpoint.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
        const ws = new WebSocket(endpoint, ['streamit.v1', ticket]);
        sockets.current.set(id, ws);
        let lastMessage = Date.now();
        ws.onopen = () => {
          if (disposed || sockets.current.get(id) !== ws) return;
          attempts.set(id, 0);
          update(id, { connected: true, error: undefined });
          beats.set(
            id,
            setInterval(() => {
              if (ws.readyState === WebSocket.OPEN) {
                if (Date.now() - lastMessage > 120000)
                  ws.close(4000, 'Reconnect after suspended connection');
                else ws.send('{"kind":"ping"}');
              }
            }, 45000),
          );
        };
        ws.onmessage = (event) => {
          if (disposed || sockets.current.get(id) !== ws) return;
          lastMessage = Date.now();
          try {
            const m = JSON.parse(event.data);
            if (m.kind === 'connected') {
              update(id, { online: m.online === true, state: m.state || null });
              clearNative(id);
              if (m.online === true) {
                receiveCapabilities(id, m.nativeSources);
                receivePreparation(id, m.nativePreparation);
              }
            }
            if (m.kind === 'presence') {
              update(id, { online: m.online === true });
              if (m.online !== true) clearNative(id);
            }
            if (m.kind === 'capabilities') receiveCapabilities(id, m.nativeSources);
            if (m.kind === 'nativePreparation') receivePreparation(id, m.preparation);
            if (m.kind === 'state') update(id, { state: m.state || null, ...(m.state ? {error: undefined} : {}) });
            if (m.kind === 'playback_error') update(id, { error: 'This source could not play. Choose another source or try again.' });
            if (m.kind === 'command_status' && !m.accepted)
              update(id, {
                error: commandError(m.code),
              });
            if (m.kind === 'ack' && !m.ok)
              update(id, {
                error: commandError(m.code),
              });
          } catch {
            update(id, { error: 'The screen sent an unreadable update.' });
          }
        };
        ws.onclose = (event) => {
          if (disposed || sockets.current.get(id) !== ws) return;
          clearInterval(beats.get(id));
          beats.delete(id);
          if (sockets.current.get(id) === ws) sockets.current.delete(id);
          update(id, { online: false, connected: false });
          clearNative(id);
          if (disposed) return;
          if (event.code === 4003) {
            void refreshRef.current();
            return;
          }
          retry(id);
        };
        ws.onerror = () =>
          { if (!disposed && sockets.current.get(id) === ws) update(id, { error: 'Connection interrupted. Reconnecting…' }); };
      } catch (error) {
        update(id, {
          online: false,
          connected: false,
          error: 'Could not connect. Retrying…',
        });
        if (error instanceof ApiError && error.status === 403) {
          void refreshRef.current();
          return;
        }
        retry(id);
      } finally {
        connecting.delete(id);
      }
    };
    const resume = () => {
      for (const id of ids.split(',').filter(Boolean)) void connect(id);
    };
    resume();
    addEventListener('online', resume);
    return () => {
      disposed = true;
      removeEventListener('online', resume);
      for (const t of timers.values()) clearTimeout(t);
      for (const b of beats.values()) clearInterval(b);
      for (const timer of preparationTimers.values()) clearTimeout(timer);
      for (const ws of sockets.current.values())
        ws.close(1000, 'Session ended');
      sockets.current.clear();
    };
  }, [userId, ids]);
  const command = useCallback((id: string, value: PlayerCommand) => {
    const ws = sockets.current.get(id);
    if (
      ws?.readyState !== WebSocket.OPEN ||
      !connectionsRef.current[id]?.online ||
      value.action === 'load'
    )
      return false;
    try {
      ws.send(
        JSON.stringify({
          kind: 'command',
          id: crypto.randomUUID(),
          expiresAt: Date.now() + 10000,
          command: value,
        }),
      );
    } catch {
      return false;
    }
    return true;
  }, []);
  return (
    <Context.Provider value={{ connections, selectedId, select, command }}>
      {children}
    </Context.Provider>
  );
}
